import React, { useEffect, useState } from "react";
import { Modal, Button, InputGroup, FormControl } from "react-bootstrap";
import API from "../../config/axios";
import { api_paths } from "../../config/api.paths";
import DatePicker from "../common/DatePicker";
import SelectField from "../common/SelectField";
import { validateData, clearError } from "../../validators/YupValidations";
import { editProfileSchema } from "../../validators/validatorSchema"; // create a schema similar to registerSchema
import { toastErrorMessage, toastSuccessMessage } from "../../utils/utils";
import EditMobileModal from "./EditMobileModal";
import OtpModal from "../auth/OtpModal";
import editIcon from "../../assets/images/icons/edit.svg";
import { SET_LOADING } from "../../redux/user";
import { useDispatch, useSelector } from "react-redux";
import { formatUSPhone } from "../../utils/phoneFormat";

const Field = ({ label, required, children, error }) => (
  <div className="mb-3 pb-1">
    <label className="form-label small mb-2">
      {label} {required && <span className="text-danger">*</span>}
    </label>
    {children}
    {error && <div className="text-danger small mt-1">{error}</div>}
  </div>
);

const EditProfileModal = ({ show, onHide, editData, refreshList }) => {
  const [form, setForm] = useState({
    first_name: "",
    middle_name: "",
    last_name: "",
    date_of_birth: null,
    gender: "",
    marital_status: "",
    phone: "",
    email: "",
    country_code: editData?.country_code || "+1",
  });

  const [errors, setErrors] = useState({});
  const [showUpdateMobileModal, setShowUpdateMobileModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpId, setOtpId] = useState(null);
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.USER_DATA);

  const genderOptions = [
    { value: "male", label: "Male" },
    { value: "female", label: "Female" },
    { value: "other", label: "Other" },
  ];

  const maritalStatusOptions = [
    { value: "single", label: "Single" },
    { value: "married", label: "Married" },
    { value: "divorced", label: "Divorced" },
    { value: "widowed", label: "Widowed" },
  ];

  const buildFormState = (data) => ({
    first_name: data?.first_name || "",
    middle_name: data?.middle_name || "",
    last_name: data?.last_name || "",
    date_of_birth: data?.date_of_birth ? new Date(data.date_of_birth) : null,
    gender: data?.gender || "",
    marital_status: data?.marital_status || "",
    phone:
      data.country_code === "+1"
        ? formatUSPhone(data.phone || "")
        : data.phone || "",
    email: data?.email || "",
    country_code: data?.country_code || "+1",
  });

  useEffect(() => {
    // Always restore the latest editData when the modal is opened,
    // so unsaved changes do not persist between opens.
    if (show && editData) {
      setForm(buildFormState(editData));
      setErrors({});
    }
  }, [show, editData]);

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (errors[field]) clearError(setErrors, field);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const isValid = await validateData(editProfileSchema, form, setErrors);
    if (!isValid) return;

    const payload = {
      first_name: form.first_name,
      middle_name: form.middle_name,
      last_name: form.last_name,
      date_of_birth: form.date_of_birth
        ? new Date(form.date_of_birth).toISOString()
        : null,
      gender: form.gender ? form.gender.toLowerCase() : "",
      marital_status: form?.marital_status?.toLowerCase() || null,
      // phone: form.phone || "",
      email: form.email || "",
    };

    try {
      dispatch(SET_LOADING(true));

      await API.put(api_paths.member.EDIT_USER, payload);

      toastSuccessMessage("Profile updated successfully");

      refreshList();
      onHide();
    } catch (error) {
      console.log("Error saving profile:", error);
      toastErrorMessage("Something went wrong while saving.");
    } finally {
      dispatch(SET_LOADING(false));
    }
  };

  const handleVerifyOtp = async (otp, otp_id) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.member.VERIFY_OTP, {
        otp,
        otp_id,
      });

      if (response.status === 201) {
        toastSuccessMessage("Mobile number updated successfully");
        // setForm({ phone: editData.phone || "" });

        setShowOtpModal(false);
        setForm((prev) => ({
          ...prev,
          phone: response.data.payload.result.phone,
          country_code: response.data.payload.result.country_code,
        }));

        // refreshList(); // refresh the profile data
      }
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to verify OTP"
      );
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  const handleResendOtp = async (otp_id) => {
    try {
      dispatch(SET_LOADING(true));
      const response = await API.post(api_paths.member.RESEND_OTP, { otp_id });
      if (response.status === 200) {
        toastSuccessMessage("OTP resent successfully");
      }
    } catch (error) {
      toastErrorMessage(
        error.response?.data?.message || "Failed to resend OTP"
      );
    } finally {
      dispatch(SET_LOADING(false)); // STOP LOADING
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="lg"
      centered
      className={
        showUpdateMobileModal || showOtpModal ? "hide-modal-dialog" : ""
      }
    >
      <Modal.Header closeButton>
        <Modal.Title>Edit Profile</Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit}>
        <Modal.Body className="px-4">
          <div className="row">
            <div className="col-md-4">
              <Field
                label="First Name"
                required
                error={errors.first_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${
                    errors.first_name ? "is-invalid" : ""
                  }`}
                  value={form.first_name}
                  onChange={(e) => handleChange("first_name", e.target.value)}
                  placeholder="Enter first name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field label="Middle Name" error={errors.middle_name?.message}>
                <input
                  type="text"
                  className={`form-control form-control-sm ${
                    errors.middle_name ? "is-invalid" : ""
                  }`}
                  value={form.middle_name}
                  onChange={(e) => handleChange("middle_name", e.target.value)}
                  placeholder="Enter middle name"
                />
              </Field>
            </div>

            <div className="col-md-4">
              <Field
                label="Last Name"
                required
                error={errors.last_name?.message}
              >
                <input
                  type="text"
                  className={`form-control form-control-sm ${
                    errors.last_name ? "is-invalid" : ""
                  }`}
                  value={form.last_name}
                  onChange={(e) => handleChange("last_name", e.target.value)}
                  placeholder="Enter last name"
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Date of Birth" required>
                <DatePicker
                  value={form.date_of_birth}
                  futureNotAllowed={true}
                  onChange={(date) => handleChange("date_of_birth", date)}
                  error={errors.date_of_birth?.message}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Gender" required error={errors.gender?.message}>
                <SelectField
                  value={
                    genderOptions.find((o) => o.value === form.gender) || null
                  }
                  options={genderOptions}
                  onChange={(opt) => handleChange("gender", opt.value)}
                />
              </Field>
            </div>

            <div className="col-md-6">
              <Field
                label="Marital Status"
                error={errors.marital_status?.message}
              >
                <SelectField
                  value={
                    maritalStatusOptions.find(
                      (o) => o.value === form.marital_status
                    ) || null
                  }
                  options={maritalStatusOptions}
                  onChange={(opt) => handleChange("marital_status", opt.value)}
                />
              </Field>
            </div>

            <div className="col-12 col-md-6">
              <Field label="Mobile Number" error={errors.phone?.message}>
                <InputGroup>
                  <FormControl
                    type="text"
                    readOnly
                    placeholder="Update via Edit"
                    className={`${errors.phone ? "is-invalid" : ""}`}
                    value={
                      form.phone ? `${form.country_code} ${form.phone}` : ""
                    }
                  />

                  {/* RIGHT SIDE ICON */}
                  <InputGroup.Text
                    role="button"
                    onClick={() => setShowUpdateMobileModal(true)}
                    className="bg-white border-start-0"
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={editIcon}
                      alt="edit"
                      style={{ width: "16px", height: "16px" }}
                    />
                  </InputGroup.Text>
                </InputGroup>

                {/* ❗ ERROR must be outside InputGroup */}
                {errors.phone && (
                  <div className="text-danger small mt-1">
                    {errors.phone.message}
                  </div>
                )}
              </Field>
            </div>

            <div className="col-md-6">
              <Field label="Email" error={errors.email?.message}>
                <input
                  type="email"
                  className={`form-control form-control-sm ${
                    errors.email ? "is-invalid" : ""
                  }`}
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  placeholder="Enter email"
                />
              </Field>
            </div>
          </div>
          <Modal.Footer className="d-flex justify-content-start gap- border-top-0 px-0">
            <Button
              type="submit"
              className="btn btn-orange-lg"
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </form>

      <EditMobileModal
        show={showUpdateMobileModal}
        onHide={() => setShowUpdateMobileModal(false)}
        editData={editData}
        refreshList={refreshList}
        onOtpOpen={(id) => {
          setOtpId(id);
          setShowOtpModal(true);
        }}
        backdrop="static"
        keyboard={false}
        className={showOtpModal ? "hide-modal-dialog" : ""}
      />

      <OtpModal
        isOpen={showOtpModal}
        onClose={() => setShowOtpModal(false)}
        onVerify={handleVerifyOtp}
        onResend={handleResendOtp}
        otp_id={otpId}
        backdrop="static"
        keyboard={false}
      />
    </Modal>
  );
};

export default EditProfileModal;
